import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Link } from 'react-router-dom';
import { 
  FileText, 
  CheckCircle, 
  AlertTriangle, 
  Shield, 
  Users,
  Scale,
  Heart,
  ArrowLeft
} from 'lucide-react';

const TermsOfService = () => {
  const lastUpdated = "January 2025";

  return (
    <div data-testid="terms-of-service-page" className="max-w-4xl mx-auto space-y-8">
      {/* Back Link */}
      <Link to="/pricing-privacy" className="inline-flex items-center text-[#78716C] hover:text-[#292524] transition-colors">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Pricing & Privacy
      </Link>

      {/* Page Header */}
      <div className="text-center mb-12">
        <h1 className="text-4xl md:text-5xl font-heading font-black tracking-tight text-[#292524] mb-4">
          Terms of <span className="text-[#B45309]">Service</span>
        </h1>
        <p className="text-lg text-[#78716C] max-w-2xl mx-auto">
          Please read these terms carefully before using CNC-Helpdesk.
        </p>
        <p className="text-sm text-[#78716C] mt-2">Last updated: {lastUpdated}</p>
      </div>

      {/* Introduction */}
      <Card className="bg-white/80 backdrop-blur-sm border-[#D6D3D1]">
        <CardContent className="pt-6">
          <p className="text-[#57534E] leading-relaxed">
            Welcome to CNC-Helpdesk. By accessing or using this application, you agree to be bound by these Terms of Service. 
            If you do not agree to these terms, please do not use the service.
          </p>
          <p className="text-[#78716C] mt-4">
            CNC-Helpdesk is operated by an independent developer and is provided as a tool to assist CNC enthusiasts, 
            hobbyists, and professionals with calculations, troubleshooting guidance, and community resources.
          </p>
        </CardContent>
      </Card>

      {/* Section 1: Acceptance of Terms */}
      <Card className="bg-gradient-to-br from-blue-500/10 to-white/60 border-blue-500/30 border-2">
        <CardHeader>
          <CardTitle className="text-xl text-[#292524] flex items-center gap-3">
            <div className="p-2 bg-blue-500/20 rounded-lg">
              <FileText className="w-5 h-5 text-blue-400" />
            </div>
            1. Acceptance of Terms
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-[#57534E]">
          <p>By creating an account or using CNC-Helpdesk, you confirm that:</p>
          <ul className="space-y-2 ml-4">
            <li className="flex items-start gap-2">
              <CheckCircle className="w-4 h-4 text-blue-400 mt-1 shrink-0" />
              <span>You are at least 18 years of age or have parental/guardian consent</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="w-4 h-4 text-blue-400 mt-1 shrink-0" />
              <span>You have read and understood these Terms of Service</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="w-4 h-4 text-blue-400 mt-1 shrink-0" />
              <span>You agree to comply with all applicable laws and regulations</span>
            </li>
          </ul>
        </CardContent>
      </Card>

      {/* Section 2: Service Description */}
      <Card className="bg-gradient-to-br from-emerald-500/10 to-white/60 border-emerald-500/30 border-2">
        <CardHeader>
          <CardTitle className="text-xl text-[#292524] flex items-center gap-3">
            <div className="p-2 bg-emerald-500/20 rounded-lg">
              <CheckCircle className="w-5 h-5 text-emerald-400" />
            </div>
            2. Service Description
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-[#57534E]">
          <p>CNC-Helpdesk provides:</p>
          <ul className="space-y-2 ml-4">
            <li className="flex items-start gap-2">
              <span className="text-emerald-400 mt-1">•</span>
              <span>CNC cutting parameter calculations and recommendations</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-emerald-400 mt-1">•</span>
              <span>Troubleshooting guidance for common CNC issues</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-emerald-400 mt-1">•</span>
              <span>Educational articles and knowledge base resources</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-emerald-400 mt-1">•</span>
              <span>Community discussion features</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-emerald-400 mt-1">•</span>
              <span>A marketplace for CNC-related items</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-emerald-400 mt-1">•</span>
              <span>AI-assisted guidance for CNC questions</span>
            </li>
          </ul>
        </CardContent>
      </Card>

      {/* Section 3: User Responsibilities */}
      <Card className="bg-gradient-to-br from-[#F59E0B]/10 to-white/60 border-[#F59E0B]/30 border-2">
        <CardHeader>
          <CardTitle className="text-xl text-[#292524] flex items-center gap-3">
            <div className="p-2 bg-[#B45309]/20 rounded-lg">
              <Users className="w-5 h-5 text-[#B45309]" />
            </div>
            3. User Responsibilities
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-[#57534E]">
          <p>As a user of CNC-Helpdesk, you agree to:</p>
          <ul className="space-y-2 ml-4">
            <li className="flex items-start gap-2">
              <span className="text-[#B45309] mt-1">•</span>
              <span>Provide accurate information when creating your account</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#B45309] mt-1">•</span>
              <span>Keep your login credentials secure and confidential</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#B45309] mt-1">•</span>
              <span>Not share your account with others (one license = one user)</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#B45309] mt-1">•</span>
              <span>Use the service respectfully and not post harmful, offensive, or illegal content</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#B45309] mt-1">•</span>
              <span>Not attempt to reverse engineer, hack, or disrupt the service</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#B45309] mt-1">•</span>
              <span>Take full responsibility for decisions made based on information from this service</span>
            </li>
          </ul>
        </CardContent>
      </Card>

      {/* Section 4: Disclaimer & Limitations */}
      <Card className="bg-gradient-to-br from-red-500/10 to-white/60 border-red-500/30 border-2">
        <CardHeader>
          <CardTitle className="text-xl text-[#292524] flex items-center gap-3">
            <div className="p-2 bg-red-500/20 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-red-400" />
            </div>
            4. Disclaimer & Limitations
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-[#57534E]">
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
            <p className="font-semibold text-[#292524] mb-2">Important Safety Notice</p>
            <p className="text-[#57534E]">
              CNC machining involves potentially dangerous equipment. The calculations, recommendations, 
              and guidance provided by CNC-Helpdesk are for <strong className="text-[#292524]">informational purposes only</strong>.
            </p>
          </div>

          <ul className="space-y-3 ml-4">
            <li className="flex items-start gap-2">
              <span className="text-red-400 mt-1">•</span>
              <span>All recommendations are <strong className="text-[#292524]">starting points</strong> and should be verified for your specific machine and material</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-red-400 mt-1">•</span>
              <span>CNC-Helpdesk does <strong className="text-[#292524]">not</strong> provide machine-specific setup, tuning, or safety inspections</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-red-400 mt-1">•</span>
              <span>Always follow manufacturer guidelines and safety procedures</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-red-400 mt-1">•</span>
              <span>Test conservative settings before running production jobs</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-red-400 mt-1">•</span>
              <span>The operator is solely responsible for safe machine operation</span>
            </li>
          </ul>

          <p className="text-[#78716C] mt-4 italic">
            CNC-Helpdesk and its developer accept no liability for damage, injury, or loss resulting from 
            the use of information provided by this service.
          </p>
        </CardContent>
      </Card>

      {/* Section 5: Intellectual Property */}
      <Card className="bg-gradient-to-br from-purple-500/10 to-white/60 border-purple-500/30 border-2">
        <CardHeader>
          <CardTitle className="text-xl text-[#292524] flex items-center gap-3">
            <div className="p-2 bg-purple-500/20 rounded-lg">
              <Shield className="w-5 h-5 text-purple-400" />
            </div>
            5. Intellectual Property
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-[#57534E]">
          <ul className="space-y-3 ml-4">
            <li className="flex items-start gap-2">
              <span className="text-purple-400 mt-1">•</span>
              <span>CNC-Helpdesk and its original content, features, and functionality are owned by the developer</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-purple-400 mt-1">•</span>
              <span>You retain ownership of content you create (marketplace listings, community posts)</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-purple-400 mt-1">•</span>
              <span>By posting content, you grant CNC-Helpdesk permission to display it within the platform</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-purple-400 mt-1">•</span>
              <span>You may not copy, reproduce, or redistribute platform content without permission</span>
            </li>
          </ul>
        </CardContent>
      </Card>

      {/* Section 6: Subscriptions & Payments */}
      <Card className="bg-gradient-to-br from-green-500/10 to-white/60 border-green-500/30 border-2">
        <CardHeader>
          <CardTitle className="text-xl text-[#292524] flex items-center gap-3">
            <div className="p-2 bg-green-500/20 rounded-lg">
              <Scale className="w-5 h-5 text-green-400" />
            </div>
            6. Subscriptions & Payments
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-[#57534E]">
          <ul className="space-y-3 ml-4">
            <li className="flex items-start gap-2">
              <span className="text-green-400 mt-1">•</span>
              <span>Subscription fees are billed in advance on a monthly or annual basis</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-400 mt-1">•</span>
              <span>Payments are processed securely by Stripe — we never see your card details</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-400 mt-1">•</span>
              <span>You may cancel your subscription at any time</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-400 mt-1">•</span>
              <span>Refunds are handled on a case-by-case basis — contact us if you have concerns</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-400 mt-1">•</span>
              <span>Prices may change with reasonable notice to existing subscribers</span>
            </li>
          </ul>
        </CardContent>
      </Card>

      {/* Section 7: Account Termination */}
      <Card className="bg-white/80 backdrop-blur-sm border-[#D6D3D1]">
        <CardHeader>
          <CardTitle className="text-xl text-[#292524] flex items-center gap-3">
            7. Account Termination
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-[#57534E]">
          <p>You may delete your account at any time from your Profile page. Upon deletion:</p>
          <ul className="space-y-2 ml-4">
            <li className="flex items-start gap-2">
              <span className="text-[#78716C] mt-1">•</span>
              <span>Your personal data will be permanently removed within 30 days</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#78716C] mt-1">•</span>
              <span>Your marketplace listings will be removed</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#78716C] mt-1">•</span>
              <span>Community posts may be anonymized rather than deleted to preserve discussion context</span>
            </li>
          </ul>
          <p className="text-[#78716C] mt-4">
            We reserve the right to suspend or terminate accounts that violate these terms.
          </p>
        </CardContent>
      </Card>

      {/* Section 8: Marketplace */}
      <Card className="bg-white/80 backdrop-blur-sm border-[#D6D3D1]">
        <CardHeader>
          <CardTitle className="text-xl text-[#292524] flex items-center gap-3">
            8. Marketplace Terms
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-[#57534E]">
          <p>The CNC-Helpdesk marketplace allows users to buy and sell CNC-related items:</p>
          <ul className="space-y-2 ml-4">
            <li className="flex items-start gap-2">
              <span className="text-[#78716C] mt-1">•</span>
              <span>CNC-Helpdesk acts as a platform only — transactions are between buyers and sellers</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#78716C] mt-1">•</span>
              <span>Sellers are responsible for accurate listings and fulfilling sales</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#78716C] mt-1">•</span>
              <span>Buyers are responsible for verifying items before purchase</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#78716C] mt-1">•</span>
              <span>Disputes should be resolved between parties — CNC-Helpdesk may assist but is not obligated</span>
            </li>
          </ul>
        </CardContent>
      </Card>

      {/* Section 9: Changes to Terms */}
      <Card className="bg-white/80 backdrop-blur-sm border-[#D6D3D1]">
        <CardHeader>
          <CardTitle className="text-xl text-[#292524] flex items-center gap-3">
            9. Changes to Terms
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-[#57534E]">
          <p>
            We may update these Terms of Service from time to time. When we do:
          </p>
          <ul className="space-y-2 ml-4">
            <li className="flex items-start gap-2">
              <span className="text-[#78716C] mt-1">•</span>
              <span>The "Last updated" date at the top of this page will change</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#78716C] mt-1">•</span>
              <span>For significant changes, we will notify users via email or in-app notification</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#78716C] mt-1">•</span>
              <span>Continued use of the service after changes constitutes acceptance of new terms</span>
            </li>
          </ul>
        </CardContent>
      </Card>

      {/* Section 10: Contact */}
      <Card className="bg-gradient-to-br from-[#F59E0B]/5 to-white/60 border-[#F59E0B]/20 border">
        <CardContent className="pt-8 pb-8">
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <div className="p-3 bg-[#B45309]/20 rounded-full">
                <Heart className="w-8 h-8 text-[#B45309]" />
              </div>
            </div>
            <h2 className="text-2xl font-bold text-[#292524] mb-4">Questions?</h2>
            <p className="text-[#57534E] max-w-xl mx-auto mb-4">
              If you have any questions about these Terms of Service, please reach out through our Contact & Feedback page.
            </p>
            <Link 
              to="/contact-feedback" 
              className="inline-flex items-center text-[#B45309] hover:text-[#B45309]/80 font-medium"
            >
              Contact Us →
            </Link>
          </div>
        </CardContent>
      </Card>

      {/* Footer spacer */}
      <div className="h-8"></div>
    </div>
  );
};

export default TermsOfService;
